<template>
  <div>
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <div>
      <p class="explanation has-text-centered">
        {{ $t("coupon.bulkCouponNote1") }}
        <a
          :href="
            'https://s3.amazonaws.com/assets.onii.com.br/models/pt/Onii-Modelo_cupom_descontos.xlsx'
          "
          target="_blank"
        >
          {{ $t("coupon.thisSpreadsheetModel") }}
        </a>
        {{ $t("coupon.bulkCouponNote2") }}
      </p>

      <div class="explanation has-text-centered">
        <b-tooltip
          :label="$t('coupon.processingFile')"
          v-if="processingProducts"
        >
          <button class="button" :disabled="true">
            <b-icon icon="file-upload"></b-icon>
            &nbsp;&nbsp;&nbsp;{{ $t("coupon.couponsSpreadsheet") }}
          </button>
        </b-tooltip>

        <button class="button" @click="$refs.bulkAddCoupons.click()" v-else>
          <b-icon icon="file-upload"></b-icon>
          &nbsp;&nbsp;&nbsp;{{ $t("coupon.couponsSpreadsheet") }}
        </button>
      </div>
    </div>
    <br />

    <input
      type="file"
      ref="bulkAddCoupons"
      id="bulk-add-coupons"
      style="display: none"
      accept=".xls,.xlsx"
      @change="bulkAddCoupons"
    />

    <h2 class="subtitle">
      {{ $t("coupon.couponTitle") }}
      &nbsp;&nbsp;
      <button
        class="button is-small"
        @click="createEditCoupon"
        style="position: relative; bottom: 2px;"
      >
        <b-icon icon="plus-circle" type="is-primary"></b-icon>
      </button>
    </h2>

    <p v-if="!storeBeingManaged.coupons.length">
      {{ $t("coupon.noCouponAtThisMoment") }}
    </p>

    <b-table v-else :data="storeBeingManaged.coupons" hoverable :key="tableKey">
      <b-table-column
        :label="$t('coupon.couponCode')"
        field="name"
        sortable
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>

      <b-table-column
        field="type"
        :label="$t('coupon.couponType')"
        sortable
        v-slot="props"
      >
        {{
          props.row.type === "absolute"
            ? $t("coupon.couponTypeAbsolute")
            : props.row.type === "percentual"
            ? $t("coupon.couponTypePercentual")
            : "-"
        }}
      </b-table-column>

      <b-table-column
        field="value"
        :label="$t('coupon.couponValue')"
        sortable
        :custom-sort="sortCouponsByValue"
        v-slot="props"
      >
        <span v-if="props.row.type === 'absolute'">
          {{ `${payments.currencySymbol} ${formatMoney(props.row.value)}` }}
        </span>
        <span v-if="props.row.type === 'percentual'">
          {{ props.row.value }}%
        </span>
      </b-table-column>

      <b-table-column
        field="qtyPerUser"
        :label="$t('coupon.couponQtyPerUser')"
        sortable
        v-slot="props"
      >
        {{ props.row.qtyPerUser }}
      </b-table-column>

      <b-table-column
        field="qtyGlobalUse"
        :label="$t('coupon.couponGlobalUseLimit')"
        sortable
        v-slot="props"
      >
        {{
          props.row.qtyGlobalUse > 0
            ? props.row.qtyGlobalUse
            : $t("coupon.couponGlobalUseUnlimited")
        }}
      </b-table-column>

      <b-table-column
        field="minimumCartValue"
        :label="$t('coupon.couponMinCartValue')"
        sortable
        v-slot="props"
      >
        {{
          `${payments.currencySymbol} ${formatMoney(
            props.row.minimumCartValue
          )}`
        }}
      </b-table-column>

      <b-table-column
        field="beginDate"
        :label="$t('coupon.couponBeginDateLabel')"
        sortable
        v-slot="props"
      >
        {{
          moment
            .utc(props.row.beginDate)
            .add(preferences.timezone, "h")
            .format("DD/MM/YY HH:mm")
        }}
      </b-table-column>

      <b-table-column
        field="endDate"
        :label="$t('coupon.couponEndDateLabel')"
        sortable
        v-slot="props"
      >
        {{
          moment
            .utc(props.row.endDate)
            .add(preferences.timezone, "h")
            .format("DD/MM/YY HH:mm")
        }}
      </b-table-column>

      <b-table-column
        field="isActive"
        :label="$t('common.status')"
        sortable
        v-slot="props"
      >
        <b-switch
          size="is-small"
          class="coupon-switch"
          type="is-primary"
          passive-type="is-secondary"
          v-model="props.row.isActive"
          @input="turnOnOffCoupon(props.row)"
        ></b-switch>

        {{ props.row.isActive ? $t("common.Active") : $t("common.Inactive") }}
      </b-table-column>

      <b-table-column :label="$t('common.edit')" v-slot="props">
        <button class="button is-small" @click="createEditCoupon(props.row)">
          <b-icon icon="edit" type="is-primary"></b-icon>
        </button>
      </b-table-column>

      <b-table-column :label="$t('common.delete')" v-slot="props">
        <button
          class="button is-small"
          @click="confirmCouponDeletion(props.row)"
        >
          <b-icon icon="minus-circle" type="is-primary"></b-icon>
        </button>
      </b-table-column>

      <template slot="empty">
        <table-empty-template />
      </template>
    </b-table>

    <b-modal :active.sync="isNewCouponModalActive">
      <new-coupon-modal
        :currentCoupon="currentCoupon"
        @increment-key="tableKey++"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../../mixins/common";
import { mask } from "../../../mixins/mask";
import newCouponModal from "./NewCouponModal";
import tableEmptyTemplate from "../../common/TableEmptyTemplate";

export default {
  mixins: [common, mask],

  data() {
    return {
      isLoading: false,
      isNewCouponModalActive: false,
      tableKey: 0,
      currentCoupon: null,
      processingProducts: false
    };
  },

  components: {
    newCouponModal,
    tableEmptyTemplate
  },

  computed: {
    ...mapGetters(["storeBeingManaged", "preferences", "payments"])
  },

  methods: {
    createEditCoupon(coupon) {
      if (coupon) {
        this.currentCoupon = JSON.parse(JSON.stringify(coupon));
        this.currentCoupon.beginDate = new Date(coupon.beginDate);
        this.currentCoupon.endDate = new Date(coupon.endDate);
      } else {
        this.currentCoupon = null;
      }
      this.isNewCouponModalActive = true;
    },

    sortCouponsByValue(a, b, isAsc) {
      const sortWeigth = o => {
        return o.type === "absolute" ? o.value / 100 : o.value;
      };
      a.v = sortWeigth(a);
      b.v = sortWeigth(b);
      return isAsc ? a.v - b.v : (a.v - b.v) * -1;
    },

    confirmCouponDeletion(coupon) {
      this.$buefy.dialog.confirm({
        message: `${this.$t("coupon.confirmCouponDeletion")} ${
          coupon.name
        } com o valor de ${
          coupon.type === "absolute"
            ? `${this.payments.currencySymbol}${coupon.value / 100}`
            : coupon.type === "percentual"
            ? `${coupon.value}%`
            : ""
        }`,
        onConfirm: () => this.deleteCoupon(coupon)
      });
    },

    deleteCoupon(coupon) {
      this.isLoading = true;
      this.$store
        .dispatch("deleteCoupon", {
          storeId: this.storeBeingManaged._id,
          couponId: coupon._id
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: this.$t("coupon.couponDeleted"),
              position: "is-bottom",
              type: "is-primary"
            });
            this.tableKey++;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    turnOnOffCoupon(coupon) {
      this.isLoading = true;
      this.$store
        .dispatch("updateCoupon", {
          storeId: this.storeBeingManaged._id,
          coupon
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
            coupon.isActive = !coupon.isActive;
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: `${this.$tc("dashboard.coupons", 1)} ${
                coupon.isActive
                  ? this.$t("common.Active")
                  : this.$t("common.Inactive")
              }`,
              position: "is-bottom",
              type: "is-primary"
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    bulkAddCoupons(event) {
      const file = this.getValidSpreadsheetFile(event);

      if (!file) return;

      const formData = new FormData();

      formData.append("file", file);
      formData.append("storeId", this.storeBeingManaged._id);

      this.isLoading = true;
      this.$store
        .dispatch("bulkAddCoupons", formData)
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.dialog.alert({
              title: this.$t("common.success"),
              message: `${this.$t("common.waitProcessingTimeNote1")} ${res.data
                .payload.minutes || 2} ${this.$t(
                "common.waitProcessingTimeNote2"
              )}`,
              type: "is-success",
              hasIcon: true,
              icon: "check",
              iconPack: "fa"
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
          // this.$emit("processing-purchases");
        });
    }
  }
};
</script>

<style scoped>
.coupon-switch {
  position: relative;
  top: 2px;
  left: 6px;
  -webkit-transform: scaleX(1);
  transform: scaleX(1);
}
</style>
